/**
 * Content shape for the CMS-backed sections, and the single source of truth for
 * it. `apps/onelive` parses Firestore reads through these schemas; `apps/cms`
 * types its FireCMS collection definitions against the inferred types. Because
 * both import this one module, a renamed field is a compile error rather than a
 * silently broken card.
 *
 * Firestore is schemaless, so nothing enforces shape at rest — a marketing user
 * can put anything in a field. That is why this is runtime validation and not
 * just generated types.
 */
import { z } from "zod";
import type { SiteKey } from "../org/sites";

/**
 * Mirrors `locales` in `apps/onelive/src/lib/i18n/config.ts`. Duplicated rather
 * than imported because `packages/shared` must not depend on an app; the two
 * lists have to be changed together.
 */
export const cmsLocales = ["zh", "jp"] as const;

export type CmsLocale = (typeof cmsLocales)[number];

/** Every user-facing string is stored per locale. */
export const LocalizedTextSchema = z.object({
    zh: z.string(),
    jp: z.string(),
});

export type LocalizedText = z.infer<typeof LocalizedTextSchema>;

/**
 * An optional field that also tolerates an explicit `null`.
 *
 * **Use this for every optional field, never a bare `.optional()`.** FireCMS
 * writes a field an editor left blank as a Firestore `nullValue`, and
 * `unwrapValue` faithfully maps that to `null`. Zod's `.optional()` accepts
 * `undefined` only, so a bare `.optional()` makes every record with an empty
 * optional field fail `safeParse` — and `fetchCollection` drops each one with a
 * warning and returns a green build.
 *
 * That is not hypothetical. The first real seeded records (2026-09-03) had
 * `img` and `order` blank, which was every optional field in this file, and the
 * one-ai build emitted zero article pages and zero tag pages while exiting 0.
 * See `apps/one-ai/src/spec/report/test-260903-res.md`.
 *
 * The `preprocess` normalises `null` to `undefined` before validation, so
 * consumers still see exactly one absent-value shape and never have to test for
 * both. A present-but-wrong value is untouched and still fails — tolerating a
 * blank is not tolerating anything.
 *
 * **`preprocess` outermost, `.optional()` inside — the order matters.** The
 * obvious `schema.nullish().transform((v) => v ?? undefined)` type-checks and
 * parses correctly, but a `transform` at the top loses the key's optionality
 * marker: the inferred type becomes `{ img: string | undefined }` rather than
 * `{ img?: string }`, so every place that builds one of these records has to
 * write `img: undefined` explicitly. Verified against zod 4.4.3 — of the three
 * plausible spellings, only this one keeps the key optional.
 */
const blankable = <T extends z.ZodType>(schema: T) =>
    z.preprocess((value) => value ?? undefined, schema.optional());

/**
 * `blankable` for a `LocalizedText` field — optional as a *whole*, and blank in
 * one of the several shapes FireCMS can write it.
 *
 * `blankable(LocalizedTextSchema)` alone is not enough, and the difference is
 * the 2026-09-03 failure mode one level deeper. A map property is a container:
 * FireCMS does not drop it when an editor clears the text inside it, it writes
 * the map with `nullValue` members — `{ zh: null, jp: null }`. The outer
 * `preprocess` never sees a `null` to normalise, `LocalizedTextSchema` demands
 * two strings, and the record drops on a green build.
 *
 * So the three blank spellings all collapse to `undefined`: the key absent,
 * the map `null`, and a map whose every member is null or empty. And because
 * the field is optional as a whole rather than per locale, a *half*-filled map
 * stays a complete `LocalizedText` with `""` for the empty locale — every
 * consumer already treats an empty string as "no description" (`buildRecordMetadata`
 * and `describe()` in jsonld.ts both do `.trim() || undefined`), so this needs
 * no new absent-value shape downstream.
 *
 * A present-but-wrong member (`{ zh: 5 }`) is passed through untouched and
 * still fails. Tolerating a blank is not tolerating anything.
 */
const blankableLocalizedText = z.preprocess((value) => {
    if (value == null) return undefined;
    if (typeof value !== "object") return value;
    const { zh, jp } = value as Record<string, unknown>;
    const text = { zh: zh ?? "", jp: jp ?? "" };
    return text.zh === "" && text.jp === "" ? undefined : text;
}, LocalizedTextSchema.optional());

/**
 * Announcement categories. An enum key, not translated text: the label lives in
 * `dictionaries/{zh,jp}.json` alongside every other UI string, so translating a
 * category never means editing content records. FireCMS renders this as a
 * dropdown, so marketing cannot invent a category by typo.
 *
 * One member today — `release` is the existing 開發公告. Adding a member here
 * plus a label in both dictionaries is the whole cost of a new category; no
 * data migration.
 */
export const announcementTypes = ["release"] as const;

export type AnnouncementType = (typeof announcementTypes)[number];

/** Drives `SectionNews` on the home page. */
export const AnnouncementSchema = z.object({
    // Stored ISO, formatted at render. Storing the display string would make
    // ordering and reformatting impossible.
    date: z.iso.date(),
    type: z.enum(announcementTypes),
    title: LocalizedTextSchema,
    url: z.url(),
});

export type Announcement = z.infer<typeof AnnouncementSchema>;

/** Drives the grid on `/case`. */
export const CaseArticleSchema = z.object({
    date: z.iso.date(),
    url: z.url(),
    // An external URL (assets.st-note.com today). Firebase Storage and a
    // FireCMS storage adapter can be added later without touching this field.
    img: z.url(),
    title: LocalizedTextSchema,
    description: LocalizedTextSchema,
});

export type CaseArticle = z.infer<typeof CaseArticleSchema>;

/**
 * The products a news article can be labelled with — stored as the value, shown
 * by the CMS as `SITES[value].name`. Each value is a `SiteKey` on purpose: a
 * product here IS one of the sites in `org/sites.ts`, so its display name is
 * written down once, there.
 *
 * A fixed list rather than a collection like `tags`, and single-valued with no
 * translations. See `docs/news-product-category.md` for the trade.
 *
 * **Adding a value is safe; removing or renaming one is not a no-op.**
 * `ArticleSchema.product` catches an unknown value to "no product", so an
 * article still holding a removed value keeps its page and loses its label.
 */
export const products = ["onelive", "chichat", "onecatch"] as const satisfies readonly SiteKey[];

export type Product = (typeof products)[number];

/**
 * One article, in either stream.
 *
 * `notes` and `news` are the same shape, so one schema serves both and
 * `ArticleSchema.extend({})` is the escape hatch if a field ever diverges — do
 * not build a second schema on speculation. The neutral name is not cosmetic: a
 * schema called "news" driving `notes` is the name-disagreement this repo
 * guards against everywhere else, and after 2026-09-16 "news" additionally
 * means a different collection than it used to.
 *
 * Unlike the two collections above, this one **owns its page** —
 * `/blog/[slug]` and `/news/[slug]`, prerendered per locale — rather than
 * linking out to note.com. That difference is why several fields below are
 * stricter than they look. The design, and the invariants each field serves,
 * are in `apps/one-ai/src/spec/publish-and-rebuild.md`.
 */
export const ArticleSchema = z.object({
    // Publish date, stored ISO and formatted at render. `fetchCollection`
    // sorts on it.
    date: z.iso.date(),
    /**
     * Written by FireCMS `autoValue: "on_update"` and unwrapped from a
     * Firestore `timestampValue`, so the sitemap's `lastmod` is a real
     * modification time. A hand-bumped field rots silently, which is exactly
     * the fabricated `lastmod` that onelive's sitemap deliberately omits.
     */
    updatedAt: z.iso.datetime(),
    /**
     * The URL segment, the same in both locale trees.
     *
     * **Assigned, not authored, as of 2026-09-07.** New articles get the next
     * whole number — `"1"`, `"2"`, … — computed by the CMS's "New article"
     * action, which also uses it as the document id (see
     * `apps/cms/src/lib/nextSlug.ts`). The regex is unchanged and deliberately
     * still permits letters: every article created before that date keeps its
     * hand-written slug, because a slug is a permanent URL and Firestore cannot
     * rename a document.
     *
     * Permanent either way. FireCMS marks the field read-only, because a static
     * export has no server to redirect an indexed URL that moved. This regex
     * is the second guard — it catches a direct Firestore write, dropping one
     * record rather than emitting a broken or duplicate URL.
     */
    slug: z.string().regex(/^[a-z0-9-]+$/),
    /**
     * Required, never optional — and deliberately stricter than `tags` below.
     *
     * A missing value has no safe default: `true` would publish a draft,
     * `false` would silently unpublish a live article. Dropping the record is
     * the only honest answer, so this must fail `safeParse` rather than
     * default either way.
     *
     * It is a **build filter, not access control.** An unpublished record is
     * absent from the site and still readable by anyone querying Firestore
     * directly. Hidden, not secret.
     */
    published: z.boolean(),
    /**
     * Tag slugs — the document ids of the `tags` collection.
     *
     * Defaulted rather than required, which is the opposite call from
     * `published` and for the opposite reason: "no tags" is unambiguous, the
     * collection ships empty while marketing populates it, and a required
     * array would drop every untagged article on a green build.
     *
     * **Not** validated against the tags collection. If parsing an article
     * depended on the tag list, one deleted tag document would drop every
     * article referencing it — so an unresolvable slug is skipped as a chip
     * and the article always renders.
     */
    tags: z.array(z.string()).default([]),
    /**
     * Hosted on One AI's R2 bucket; the CMS autofills this from the presigned
     * upload. Optional because the listing rows carry no image — this is for
     * the detail page and `og:image`.
     */
    img: blankable(z.url()),
    /**
     * Which product the article is about. **News stream only** — the CMS
     * offers the field on news articles alone, so a blog article simply never
     * carries one. On the shared schema anyway, rather than an
     * `ArticleSchema.extend({})`, because an optional field is not worth
     * threading a second schema through `fetchArticles` and every consumer.
     *
     * Read by the product sites since 2026-09-24. `apps/onelive` and
     * `apps/chichat` each show the news labelled with their own product
     * (`cms/product-news.ts`). `apps/one-ai` renders nothing from it yet.
     *
     * Two parts of this line are load-bearing:
     *
     * - **`blankable`** — every article written before the field existed lacks
     *   it, and FireCMS writes `null` when an editor clears the dropdown.
     * - **`.catch(undefined)`** — a value removed from `products` would
     *   otherwise fail `safeParse` and drop the record, which in `apps/one-ai`
     *   is a missing prerendered page. It degrades to "no product" instead.
     *   The CMS dropdown already restricts new values, so this only ever
     *   applies to historical records.
     */
    product: blankable(z.enum(products).optional().catch(undefined)),
    title: LocalizedTextSchema,
    /**
     * The meta description and the listing excerpt. Its own field rather than
     * a truncation of `body`, which is HTML and truncates into garbage — which
     * is also why an absent one has no fallback: no description at all beats a
     * mangled one, and Google composes its own snippet from the page.
     *
     * **Optional since 2026-09-07**, by request. Optional as a whole rather
     * than per locale: see `blankableLocalizedText` for what a half-filled map
     * does. Every consumer already treats the empty string as absent, so the
     * three of them — both `[slug]/page.tsx` and `articleGraph` — needed only
     * to stop asserting it is there.
     */
    description: blankableLocalizedText,
    /**
     * HTML, written by the Lexical editor in `apps/cms` — not markdown. It was
     * markdown-with-HTML until 2026-09-07; see `apps/cms/src/fields/
     * RichTextField.tsx` for what changed and why.
     *
     * The type does not encode that, because it never did: this was always a
     * `string`, and a Zod schema cannot tell one string format from another
     * usefully. So the format is a convention held in three places that must
     * agree — the editor that writes it, `apps/one-ai/src/lib/cms/html.ts`
     * that sanitises it, and this comment. There is no markdown parser between
     * the store and the page any more, which makes that sanitiser the entire
     * guard rather than one of two. See invariant 7 in the spec.
     */
    body: LocalizedTextSchema,
});

export type Article = z.infer<typeof ArticleSchema>;

/**
 * A news tag.
 *
 * The **document id is the slug** (`customId: true` in FireCMS), so it is not
 * a field here: `WithId` hands it back for free, and a document id cannot be
 * edited, which makes tag slugs permanent with no validation rule.
 *
 * **Assigned, not authored, as of 2026-09-08** — the same change articles got
 * the day before. A new tag's id is the next whole number, computed by the
 * CMS's "New tag" action (`apps/cms/src/lib/nextSlug.ts`), so `/news/tag/3` is
 * the URL and `label` is the only human-readable handle a tag has. Tags created
 * before that date keep their written slug, because Firestore cannot rename a
 * document. Nothing here narrows to digits: `ArticleSchema.tags` is
 * `z.array(z.string())` on purpose (an unresolvable slug is skipped, never
 * fatal), and the id is not a field to validate.
 *
 * A collection rather than a `z.enum` like `announcementTypes` above — the
 * whole point is that an editor adds a tag without a code change and a deploy.
 */
export const TagSchema = z.object({
    // Present because `fetchCollection` is <T extends { date: string }> and
    // sorts on it. Creation date is the meaningful value to put there.
    date: z.iso.date(),
    /**
     * The chip text, per locale.
     *
     * Tag labels are **content, not UI copy** — the opposite of
     * `announcementTypes`, whose labels live in the site dictionaries. They sit
     * on the document so adding a tag needs no code change; the trade is that
     * they skip the repo's translation review.
     */
    label: LocalizedTextSchema,
    /**
     * Whether this tag is one of the news page's CATEGORIES — the pills in
     * `TagFilterBar`.
     *
     * The filter bar is the only surface it gates. A non-category tag keeps its
     * prerendered `/news/tag/<id>` page, its sitemap entry and its chips on
     * every article carrying it; it simply is not offered as a filter. So this
     * is a curation flag for one card, and `retired` below remains the single
     * visibility flag — `apps/one-ai`'s `categoryTags` narrows `visibleTags`
     * rather than replacing it.
     *
     * **Defaulted where `retired` is required, and the asymmetry is
     * deliberate.** Every tag document written before 2026-09-10 has no such
     * field, and a required flag would fail `safeParse` on all of them — which
     * `assertParsedSomething` turns into a red one-ai build rather than a
     * missing card. "Not a category" is also unambiguous in a way neither
     * `retired` value is: it hides nothing that was visible and publishes
     * nothing that was not, exactly the reasoning `ArticleSchema.tags`
     * uses for its own default. A present-but-wrong value still fails.
     *
     * Consequence worth knowing in the CMS: a Firestore `where` skips a
     * document missing the field, so a `category == false` column filter does
     * not list tags created before this field existed. Saving one writes the
     * explicit `false`.
     */
    category: z.boolean().default(false),
    /**
     * Hides the tag everywhere public: no chip, no filter link, no prerendered
     * page. An archive flag, not a soft delete — nothing about a retired tag
     * renders, so it preserves no history. What it buys is picker hygiene and
     * reversibility.
     */
    retired: z.boolean(),
    /** Explicit ordering for the filter bar. Alphabetical when unset. */
    order: blankable(z.number()),
});

export type Tag = z.infer<typeof TagSchema>;

/**
 * Locale is only known after hydration on these sites (client-side i18n, no
 * `[lang]` routing), so server components pass whole `LocalizedText` objects
 * down and the client component picks with this.
 */
export const pickLocale = (text: LocalizedText, locale: CmsLocale): string =>
    text[locale];

/**
 * `pickLocale` for an optional field, collapsing "no field" and "this locale
 * is empty" into one `undefined`.
 *
 * Those two are genuinely the same thing to every caller — an absent
 * description and an untranslated one both mean "ship no description here" —
 * and returning `""` for the second would make each call site remember to
 * `.trim()` before using it. One of them forgetting is a `content=""` meta tag,
 * which is worse for a snippet than having no tag at all.
 */
export const pickOptionalLocale = (
    text: LocalizedText | undefined,
    locale: CmsLocale,
): string | undefined => text?.[locale].trim() || undefined;
